import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  of,
  throwError,
  firstValueFrom,
} from 'rxjs';
import { catchError, tap, retry, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SecureKeyManagementService } from './secure-key-management.service';
import { setEncryptionKey, setEnvironment, decryptConfiguration, EncryptedData, isValidEncryptedData } from '../utils/encryption-helper';

/**
 * MFA enforcement posture for the dashboard sign-in flow.
 * - 'off': MFA is opt-in only (My Account > Security)
 * - 'prompt': users without a second factor see a dismissible enrollment banner
 * - 'required': users without a second factor are routed to /enroll-mfa
 */
export type MfaEnforcementMode = 'off' | 'prompt' | 'required';

export interface RuntimeConfiguration {
  services: {
    baseFunction: string;
    msspFunction: string;
    alertsFunction: string;
    signalrService: string;
    emailService: string;
    notificationService: string;
  };
  security: {
    sessionTimeout: number;
    refreshThreshold: number;
    maxFailedAttempts: number;
    lockoutDuration: number;
    tokenRefreshInterval: number;
    /** MFA enforcement mode; optional for older config payloads (default 'off'). */
    mfaEnforcement?: MfaEnforcementMode;
  };
  rateLimit: {
    maxRequestsPerMinute: number;
    maxRequestsPerHour: number;
    burstLimit: number;
  };
  features: {
    enableAdvancedNotifications: boolean;
    enableRealTimeUpdates: boolean;
    enableAiAnalysis: boolean;
    enableComplianceReporting: boolean;
    enableMfaRequired: boolean;
  };
  monitoring: {
    enableHealthChecks: boolean;
    healthCheckInterval: number;
    enablePerformanceMonitoring: boolean;
    enableSecurityMonitoring: boolean;
  };
  csp: {
    [key: string]: string;
  };
  secrets: {
    stripePublicKey: string;
    applicationInsightsConnectionString: string;
    /** Host key for THIS API function app (func-scanninja-api-dev/prd). */
    apiFunctionToken: string;
    /** LEGACY: host key for func-scanninja-dev/prd (the CVE function app), NOT this API. */
    baseFunctionToken: string;
  };
}

/**
 * RuntimeConfigurationService - Secure runtime config loader with client-side decryption
 *
 * Boundary: Does NOT store secrets in bundle/environment files - all sensitive config fetched at runtime
 * External dependencies:
 * - API endpoint: GET /api/configuration (returns encrypted RuntimeConfiguration)
 * - SecureKeyManagementService: Provides decryption key
 * - encryption-helper util: Decrypts API response
 * - sessionStorage: Reads 'sn_session' for auth token (TODO: Replace with proper auth service)
 */

/**
 * Fetches and decrypts application configuration from API at startup
 *
 * Dependencies:
 * - HttpClient: API communication with retry logic
 * - SecureKeyManagementService: Encryption key provider
 *
 * Side effects:
 * - BehaviorSubjects: configurationSubject, isLoadingSubject, errorSubject (public observables)
 * - Cache: configCache object with 5-minute TTL
 * - sessionStorage: Reads 'sn_session' for auth token in getAuthToken()
 * - Encryption: Sets global encryption key via setEncryptionKey() during initialize()
 * - Fallback config: Returns hardcoded config in non-production if API fails
 */
@Injectable({
  providedIn: 'root',
})
export class RuntimeConfigurationService {
  private configurationSubject =
    new BehaviorSubject<RuntimeConfiguration | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);

  // Cache configuration for 5 minutes
  private readonly CACHE_DURATION = 5 * 60 * 1000;
  private configCache: { data: RuntimeConfiguration; expiry: number } | null =
    null;

  // Public observables
  public configuration$ = this.configurationSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();
  public error$ = this.errorSubject.asObservable();

  private http = inject(HttpClient);
  private secureKeyService = inject(SecureKeyManagementService);
  private platformId = inject(PLATFORM_ID);

  /**
   * Initializes encryption key and loads configuration (call during app startup)
   *
   * @returns Promise resolving to true if config loaded successfully, false on error
   * - State touched: Sets global encryption key, populates configurationSubject
   * - Error handling: Logs to console.error, returns false (app can continue with fallback)
   * - Idempotency: Safe to call multiple times (uses cache)
   * - Side effects: Calls initializeEncryptionKey() then loadConfiguration()
   *
   * @example
   * // In APP_INITIALIZER
   * export function initializeApp(configService: RuntimeConfigurationService) {
   *   return () => configService.initialize();
   * }
   */
  async initialize(): Promise<boolean> {
    // SSR: Skip initialization on server-side - return mock success
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('⚠️ Running on server - skipping runtime configuration initialization');
      return true;
    }

    try {
      // initializeEncryptionKey() prepares the client-side key needed to decrypt
      // the encrypted runtime configuration response.
      await this.initializeEncryptionKey();

      // The configuration endpoint is anonymous bootstrap infrastructure; do
      // not attach bearer tokens or Functions keys. Those headers force browser
      // preflight requests and add avoidable startup latency.
      const authHeaders = await this.getAuthHeadersAsync();
      const config = await firstValueFrom(this.loadConfiguration(authHeaders));
      return !!config;
    } catch (error) {
      console.error('Failed to initialize runtime configuration:', error);
      return false;
    }
  }

  /**
   * Initialize encryption key for secure configuration
   * @private
   */
  private async initializeEncryptionKey(): Promise<void> {
    try {

      // Set environment for fallback key generation
      const env = environment.production ? 'production' : 'dev';
      setEnvironment(env);

      // Get encryption key from secure service
      const encryptionKey = await this.secureKeyService.getEncryptionKey();
      if (encryptionKey) {
        setEncryptionKey(encryptionKey);
      } else {
        console.warn('⚠️ No encryption key available, using fallback');
      }

      // Debug: Log encryption diagnostics
      const { getEncryptionDiagnostics } = await import('../utils/encryption-helper');
      const _diagnostics = getEncryptionDiagnostics();

    } catch (error) {
      console.error('Failed to initialize encryption key:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      // Continue without encryption key - fallback will be used
    }
  }

  /**
   * Fetches configuration from API with retry logic, decrypts, and caches result
   *
   * @returns Observable emitting RuntimeConfiguration on success
   * - Observable semantics: Emits once then completes, uses cached value if not expired
   * - HTTP errors: Retries 3x with exponential backoff (2^n seconds), falls back to getFallbackConfiguration()
   * - State touched: Updates configCache with 5-min TTL, emits to configurationSubject/isLoadingSubject/errorSubject
   * - Idempotency: Safe to call multiple times (cache prevents redundant API calls)
   * - API endpoint: ${configApi.baseUrl}${configApi.endpoint} or environment.app_api_url/api/configuration
   * - Decryption: Detects encrypted response (isValidEncryptedData), decrypts via decryptConfiguration()
   * - Fallback: Returns hardcoded config in dev, throws error in production if API fails
    * - Auth: Does not attach auth headers because the endpoint is anonymous bootstrap infrastructure
   */
  loadConfiguration(prebuiltHeaders?: HttpHeaders): Observable<RuntimeConfiguration> {
    // Check cache first
    if (this.configCache && Date.now() < this.configCache.expiry) {
      this.configurationSubject.next(this.configCache.data);
      return of(this.configCache.data);
    }

    this.isLoadingSubject.next(true);
    this.errorSubject.next(null);
    const configApi = (environment as any).configApi || {
      baseUrl: environment.app_api_url,
      endpoint: '/api/configuration',
    };

    const url = `${configApi.baseUrl}${configApi.endpoint}`;
    const headers = prebuiltHeaders ?? this.getAuthHeaders();

    return this.http
      .get<RuntimeConfiguration | EncryptedData>(url, { headers })
      .pipe(
        retry({
          count: 3,
          delay: (error, retryCount) => {
            const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff
            return new Promise((resolve) => setTimeout(resolve, delay));
          },
        }),
        switchMap((response) => {

          // Check if response is encrypted
          if (isValidEncryptedData(response)) {
            // Convert promise to observable using RxJS from()
            return new Observable<RuntimeConfiguration>((observer) => {
              decryptConfiguration<RuntimeConfiguration>(response as EncryptedData)
                .then((decryptedConfig) => {
                  observer.next(decryptedConfig);
                  observer.complete();
                })
                .catch((error) => {
                  console.error('❌ Failed to decrypt configuration:', error);
                  observer.error(new Error('Configuration decryption failed: ' + (error instanceof Error ? error.message : 'Unknown error')));
                });
            });
          } else {
            // Response is already decrypted (shouldn't happen in production)
            return of(response as RuntimeConfiguration);
          }
        }),
        tap((config) => {

          // Cache the configuration
          this.configCache = {
            data: config,
            expiry: Date.now() + this.CACHE_DURATION,
          };

          // Propagate service URLs and tokens from API config to environment so services
          // that cache from environment pick up the KV-backed values at startup.
          if (config?.services?.emailService) {
            environment.emailServiceUrl = config.services.emailService;
          }
          // apiFunctionToken is the host key for the Dashboard API function app
          // (func-scanninja-api-dev/prd) — the host that environment.app_api_url
          // points at, and therefore the only key FunctionsKeyInterceptor may send.
          //
          // Do NOT fall back to baseFunctionToken here: despite the name it is the
          // host key for a *different* app (func-scanninja-dev/prd, the CVE
          // function), so sending it makes the API host reject every request with a
          // bare 401 before any function code — and before withAuth — ever runs.
          // See issue #677 (SSP generate 401 in Production).
          if (config?.secrets?.apiFunctionToken) {
            (environment as any).app_api_token = config.secrets.apiFunctionToken;
          } else {
            console.error(
              '❌ apiFunctionToken missing from runtime configuration — calls to the API function app will fail with 401.',
            );
          }

          this.configurationSubject.next(config);
          this.isLoadingSubject.next(false);

          // Runtime configuration loaded successfully
        }),
        catchError((error) => {
          this.isLoadingSubject.next(false);

          const fallbackConfig = this.getFallbackConfiguration();
          const errorMessage = this.handleConfigurationError(error);

          this.errorSubject.next(errorMessage);

          // Use fallback configuration only in non-production environments
          if (fallbackConfig) {
            console.warn(
              '⚠️ Using fallback configuration due to error:',
              errorMessage,
            );
            this.configurationSubject.next(fallbackConfig);
            return of(fallbackConfig);
          }

          return throwError(() => new Error(errorMessage));
        }),
      );
  }

  /**
   * Returns current cached configuration synchronously (null if not loaded)
   *
   * @returns RuntimeConfiguration object or null if not yet initialized
   * - Use case: Quick access without Observable subscription
   * - Race condition: May return null during app startup, prefer waitForConfiguration() for safety
   */
  getCurrentConfiguration(): RuntimeConfiguration | null {
    return this.configurationSubject.value;
  }

  /**
   * Get specific configuration section
   */
  getServiceConfiguration(): { [key: string]: string } | null {
    const config = this.getCurrentConfiguration();
    return config ? config.services : null;
  }

  getSecurityConfiguration(): RuntimeConfiguration['security'] | null {
    const config = this.getCurrentConfiguration();
    return config ? config.security : null;
  }

  /**
   * Returns the configured MFA enforcement mode, defaulting to 'off' when the
   * config has not loaded yet or the field is absent from the payload.
   *
   * @returns 'off' | 'prompt' | 'required'
   * - Safe to call before configuration is loaded (returns 'off')
   */
  getMfaEnforcement(): MfaEnforcementMode {
    return this.getSecurityConfiguration()?.mfaEnforcement ?? 'off';
  }

  getFeatureFlags(): RuntimeConfiguration['features'] | null {
    const config = this.getCurrentConfiguration();
    return config ? config.features : null;
  }

  getSecrets(): RuntimeConfiguration['secrets'] | null {
    const config = this.getCurrentConfiguration();
    return config ? config.secrets : null;
  }

  /**
   * Checks if a feature flag is enabled in current configuration
   *
   * @param featureName - Key from RuntimeConfiguration.features object
   * @returns True if feature enabled, false if disabled or config not loaded
   * - Safe to call before config loaded (returns false)
   * - Use case: Conditional rendering, feature availability checks
   */
  isFeatureEnabled(
    featureName: keyof RuntimeConfiguration['features'],
  ): boolean {
    const features = this.getFeatureFlags();
    return features ? features[featureName] : false;
  }

  /**
   * Get service URL by name
   */
  getServiceUrl(
    serviceName: keyof RuntimeConfiguration['services'],
  ): string | null {
    const services = this.getServiceConfiguration();
    return services ? services[serviceName] : null;
  }

  /**
   * Bypasses cache and fetches fresh configuration from API
   *
   * @returns Observable emitting updated RuntimeConfiguration
   * - State touched: Nullifies configCache, calls loadConfiguration()
   * - Use case: Force refresh after config change, manual cache invalidation
   * - Idempotency: Safe to call repeatedly (triggers API call each time)
   */
  refreshConfiguration(): Observable<RuntimeConfiguration> {
    this.configCache = null; // Clear cache
    return this.loadConfiguration();
  }

  /**
   * Build authentication headers for the /api/configuration request.
    * The endpoint is anonymous, so this intentionally returns no auth or
    * Functions-key headers to avoid CORS preflights during bootstrap.
   */
  private async getAuthHeadersAsync(): Promise<HttpHeaders> {
    return new HttpHeaders();
  }

  /**
   * @deprecated Use getAuthHeadersAsync() instead. Kept only to satisfy loadConfiguration()
   * which calls it synchronously inside the RxJS pipe. The async version is used by initialize().
   */
  private getAuthHeaders(): HttpHeaders {
    return new HttpHeaders();
  }

  /**
   * Handle configuration loading errors
   */
  private handleConfigurationError(error: HttpErrorResponse): string {
    if (error.status === 401) {
      return 'Authentication required to load configuration';
    } else if (error.status === 403) {
      return 'Access denied to configuration';
    } else if (error.status === 404) {
      return 'Configuration endpoint not found';
    } else if (error.status >= 500) {
      return 'Server error loading configuration';
    } else if (error.error instanceof ErrorEvent) {
      return 'Network error loading configuration';
    } else {
      return 'Failed to load application configuration';
    }
  }

  /**
   * Get fallback configuration for offline/error scenarios
   */
  private getFallbackConfiguration(): RuntimeConfiguration | null {
    // Provide minimal fallback configuration to keep app functional
    // This should be basic configuration that allows core functionality


    // TODO Move thes to the proper environment urls
    return {
      services: {
        baseFunction: environment.production
          ? 'https://cve.scanninja.app'
          : 'https://dev.cve.scanninja.app',
        msspFunction: environment.production
          ? 'https://mssp.scanninja.app'
          : 'https://dev.mssp.scanninja.app',
        alertsFunction: environment.production
          ? 'https://alerts.scanninja.app/api'
          : 'https://alerts.scanninja.app/api',
        signalrService: environment.production
          ? 'https://signalr-scanninja-alerts-prd.service.signalr.net/api'
          : 'https://signalr-scanninja-alerts-dev.service.signalr.net/api',
        emailService: environment.production
          ? 'https://func-scanninja-email-prd.azurewebsites.net/api'
          : 'https://func-scanninja-email-dev.azurewebsites.net/api',
        notificationService: environment.production
          ? 'https://cve.scanninja.app/api/'
          : 'https://dev.cve.scanninja.app/api/',
      },
      security: {
        sessionTimeout: 30 * 60 * 1000, // 30 minutes
        refreshThreshold: 5 * 60 * 1000, // 5 minutes
        maxFailedAttempts: 5,
        lockoutDuration: 15 * 60 * 1000, // 15 minutes
        tokenRefreshInterval: 25 * 60 * 1000, // 25 minutes
        mfaEnforcement: 'off', // Conservative default: MFA remains opt-in
      },
      rateLimit: {
        maxRequestsPerMinute: 100,
        maxRequestsPerHour: 1000,
        burstLimit: 20,
      },
      features: {
        enableAdvancedNotifications: false, // Conservative defaults
        enableRealTimeUpdates: false,
        enableAiAnalysis: false,
        enableComplianceReporting: false,
        enableMfaRequired: environment.production,
      },
      monitoring: {
        enableHealthChecks: false,
        healthCheckInterval: 60000,
        enablePerformanceMonitoring: false,
        enableSecurityMonitoring: false,
      },
      csp: {
        'default-src': "'self'",
        'script-src':
          "'self' https://www.googletagmanager.com https://js.stripe.com",
        'style-src': "'self' 'unsafe-inline' https://unpkg.com",
        'img-src': "'self' data:",
        'connect-src':
          "'self' https://www.googletagmanager.com https://js.stripe.com https://unpkg.com",
      },
      secrets: {
        stripePublicKey: '', // Empty - payment features will be disabled
        applicationInsightsConnectionString: '',
        apiFunctionToken: '',
        baseFunctionToken: '',
      },
    };
  }

  /**
   * Check if configuration is loaded and valid
   */
  isConfigurationLoaded(): boolean {
    return this.configurationSubject.value !== null;
  }

  /**
   * Returns Promise that resolves when configuration is loaded (or rejects on error)
   *
   * @returns Promise<RuntimeConfiguration> resolving to loaded config
   * - Resolves immediately if config already loaded
   * - Subscribes to configuration$ and error$ streams, waits for emit
   * - Auto-triggers loadConfiguration() if not already loading
   * - Rejects if error$ emits before configuration$ emits
   * - Use case: APP_INITIALIZER, guards that require config
   *
   * @example
   * // In route guard
   * async canActivate() {
   *   const config = await this.configService.waitForConfiguration();
   *   return config.features.enableFeature;
   * }
   */
  waitForConfiguration(): Promise<RuntimeConfiguration> {
    const currentConfig = this.getCurrentConfiguration();

    if (currentConfig) {
      return Promise.resolve(currentConfig);
    }

    return new Promise((resolve, reject) => {
      const subscription = this.configuration$.subscribe((config) => {
        if (config) {
          subscription.unsubscribe();
          resolve(config);
        }
      });

      const errorSubscription = this.error$.subscribe((error) => {
        if (error) {
          subscription.unsubscribe();
          errorSubscription.unsubscribe();
          reject(new Error(error));
        }
      });

      // Start loading if not already loading
      if (!this.isLoadingSubject.value) {
        this.loadConfiguration().subscribe();
      }
    });
  }

  /**
   * Clears cache and resets configuration state (forces re-fetch on next access)
   *
   * - State touched: Nullifies configCache, sets configurationSubject/errorSubject to null
   * - Use case: Logout, company switch, cache corruption
   * - Does not stop in-flight requests
   */
  clearCache(): void {
    this.configCache = null;
    this.configurationSubject.next(null);
    this.errorSubject.next(null);
  }
}
